const WEIGHT_NAMES = [
  "abrahamweight",
  "fortressweight",
  "sandpweight",
  "barclaysweight",
  "btop50weight",
  "seventythirty"
];

const RATE_NAMES = {
  abraham: "Abraham",
  fortress: "Fortress",
  sandp: "S&P500",
  barclays: "Barclays LT Bond Index",
  btop50: "BTOP50",
  seventythirty: "70/30"
};

function getJson(url) {
  const separator = url.indexOf("?") === -1 ? "?" : "&";
  const cacheBustedUrl = url + separator + new Date().getTime();

  return fetch(cacheBustedUrl).then(function(response) {
    if (!response.ok) {
      throw new Error("Unable to fetch performance data: " + response.status);
    }

    return response.json();
  });
}

export function PerformanceRates(url, drawdownUrl, fortressReturnsUrl) {
  this.url = url;
  this.drawdownUrl = drawdownUrl;
  this.fortressReturnsUrl = fortressReturnsUrl;
  this.rates = [];
  this.drawdownRates = [];
  this.fortressReturnRates = [];
}

PerformanceRates.prototype.filterByDate = function(dateBoundary) {
  return this.rates.filter(function(rate) {
    return dateFns.compareAsc(new Date(rate.Date), new Date(dateBoundary)) >= 0;
  });
};

PerformanceRates.prototype.fetch = function() {
  return Promise.all([
    getJson(this.url),
    getJson(this.drawdownUrl),
    getJson(this.fortressReturnsUrl)
  ]).then(function(responses) {
    this.rates = responses[0];
    this.drawdownRates = responses[1];
    this.fortressReturnRates = responses[2];
    return this.rates;
  }.bind(this));
};

PerformanceRates.prototype.calculate = function(weights, dateFilter) {
  const parsedWeights = {};
  const currentRates = this.filterByDate(dateFilter || "01/01/1980");

  Object.keys(weights).forEach(function(weightName) {
    if (WEIGHT_NAMES.includes(weightName)) {
      parsedWeights[weightName.replace("weight", "")] = parseInt(
        weights[weightName],
        10
      );
    }
  });

  const weightAmounts = [];
  const weightsForDeviation = [];
  const drawDown = [];

  for (let i = 0; i < currentRates.length; i++) {
    const multiplier = weightAmounts[i - 1] || 1000;
    let weightAmount = 0;

    if (Object.keys(parsedWeights).length === 1) {
      weightAmount = currentRates[i][RATE_NAMES[Object.keys(parsedWeights)[0]]];
    } else {
      weightAmount =
        multiplier *
        this.calcSingleRate(parsedWeights, currentRates[i], currentRates[i - 1]);
    }

    if (isNaN(weightAmount)) {
      continue;
    }

    weightAmounts.push(parseFloat(weightAmount.toFixed(2)));

    if (weightAmounts[i - 1]) {
      const variationAmountWeight = weightAmounts[i] - weightAmounts[i - 1];
      const profitWeightPercentage =
        (variationAmountWeight * 100) / weightAmounts[i - 1];
      weightsForDeviation.push(profitWeightPercentage);

      const drawdownCurrent =
        weightAmounts[i] / Math.max.apply(null, weightAmounts) - 1;
      drawDown.push(drawdownCurrent);
    }
  }

  const inception = this.inception(weightAmounts);
  const annualReturns = this.annualReturns(weightAmounts);
  const standardDeviation = parseFloat(
    this.standardDeviation(weightsForDeviation).toFixed(2)
  );
  const sharpRatio = this.sharpeRation(annualReturns, standardDeviation);
  const drawdownResult = parseFloat(this.maxDrawdown(drawDown).toFixed(2));

  return {
    inception: inception,
    annualReturns: annualReturns,
    standardDeviation: standardDeviation,
    sharpRatio: sharpRatio,
    drawdownResult: drawdownResult,
    rates: currentRates,
    chart: weightAmounts
  };
};

PerformanceRates.prototype.inception = function(weightAmounts) {
  return (
    ((weightAmounts[weightAmounts.length - 1] - weightAmounts[0]) * 100) /
    weightAmounts[0]
  );
};

PerformanceRates.prototype.annualReturns = function(weightAmounts) {
  return parseFloat(
    (
      (Math.pow(
        weightAmounts[weightAmounts.length - 1] / weightAmounts[0],
        1 / (weightAmounts.length / 12)
      ) -
        1) *
      100.8
    ).toFixed(2)
  );
};

PerformanceRates.prototype.standardDeviation = function(weightsForDeviation) {
  const sum = weightsForDeviation.reduce(function(acc, cur) {
    return acc + cur;
  }, 0);
  const mean = sum / weightsForDeviation.length;
  let variance = 0;

  weightsForDeviation.forEach(function(value) {
    variance += Math.pow(value - mean, 2);
  });

  return (
    parseFloat(Math.sqrt(variance) / Math.sqrt(weightsForDeviation.length - 1)) *
    Math.sqrt(12)
  );
};

PerformanceRates.prototype.sharpeRation = function(annualReturns, standardDeviation) {
  return parseFloat(annualReturns / standardDeviation).toFixed(2);
};

PerformanceRates.prototype.maxDrawdown = function(drawDownArray) {
  return Math.min.apply(null, drawDownArray) * 100;
};

PerformanceRates.prototype.calcSingleRate = function(parsedWeights, currentRate, prevRate) {
  let weightAmount = 0;

  Object.keys(parsedWeights).forEach(function(rateName) {
    const rateWeight = parsedWeights[rateName] / 100;
    let profitablePercentage = 0;

    if (prevRate) {
      const variation =
        parseInt(currentRate[RATE_NAMES[rateName]] || 1, 10) -
        parseInt(prevRate[RATE_NAMES[rateName]] || 1, 10);
      profitablePercentage = parseFloat(
        ((variation * 100) / (prevRate[RATE_NAMES[rateName]] || 1)).toFixed(2)
      );
    }

    weightAmount += rateWeight * (1 + profitablePercentage / 100);
  });

  return weightAmount;
};
